import * as React from "react"
import { type DialogProps } from "@radix-ui/react-dialog"
import { Command as CommandPrimitive } from "cmdk"
import { Search } from "lucide-react"

import { cn } from "@/lib/utils"
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog"
import { shouldSuppressAutoFocus } from "@/hooks/use-mobile"

import { laListe, listeDuChamp, useDesigneLOptionActive } from "./command-clavier"

const Command = React.forwardRef<
  React.ElementRef<typeof CommandPrimitive>,
  React.ComponentPropsWithoutRef<typeof CommandPrimitive>
>(({ className, ...props }, ref) => (
  <CommandPrimitive
    ref={ref}
    className={cn(
      "flex h-full w-full flex-col overflow-hidden rounded-md bg-popover text-popover-foreground",
      className
    )}
    {...props}
  />
))
Command.displayName = CommandPrimitive.displayName

const CommandDialog = ({
  children,
  ...props
}: DialogProps & { children: React.ReactNode }) => {
  return (
    <Dialog {...props}>
      <DialogContent className="overflow-hidden p-0 shadow-lg">
        <DialogTitle className="sr-only">Command</DialogTitle>
        <Command className="[&_[cmdk-group-heading]]:px-2 [&_[cmdk-group-heading]]:font-medium [&_[cmdk-group-heading]]:text-muted-foreground [&_[cmdk-group]]:px-2 [&_[cmdk-input-wrapper]_svg]:h-5 [&_[cmdk-input-wrapper]_svg]:w-5 [&_[cmdk-input]]:h-12 [&_[cmdk-item]]:px-2 [&_[cmdk-item]]:py-3 [&_[cmdk-item]_svg]:h-5 [&_[cmdk-item]_svg]:w-5">
          {children}
        </Command>
      </DialogContent>
    </Dialog>
  )
}

/**
 * Le champ de recherche. ⚠️ Son `aria-activedescendant` n'est pas celui de cmdk, qui ne
 * désigne rien, ou une option périmée, à l'ouverture et après une recherche : il est
 * resynchronisé sur l'option réellement active (`./command-clavier` en donne la cause).
 */
const CommandInput = React.forwardRef<
  React.ElementRef<typeof CommandPrimitive.Input>,
  React.ComponentPropsWithoutRef<typeof CommandPrimitive.Input>
>(({ className, autoFocus, ...props }, ref) => {
  const designeLOptionActive = useDesigneLOptionActive(ref, listeDuChamp)
  return (
    <div className="flex items-center border-b px-3" cmdk-input-wrapper="">
      <Search className="mr-2 h-4 w-4 shrink-0 opacity-50" />
      <CommandPrimitive.Input
        ref={designeLOptionActive}
        // TECH-011 — Mobile/tactile : neutralise autoFocus (le clavier virtuel
        // surgirait à l'ouverture du combobox et masquerait la liste).
        autoFocus={autoFocus && !shouldSuppressAutoFocus() ? true : undefined}
        className={cn(
          // text-base sur mobile (16px) → pas de zoom auto iOS au focus ; md:text-sm desktop.
          "flex h-9 w-full rounded-md bg-transparent py-3 text-base md:text-sm outline-none placeholder:text-muted-foreground disabled:cursor-not-allowed disabled:opacity-50",
          className
        )}
        {...props}
      />
    </div>
  )
})
CommandInput.displayName = CommandPrimitive.Input.displayName

/**
 * La liste. Son `aria-activedescendant` est resynchronisé comme celui du champ, et elle ramène
 * dans la vue l'option qu'une recherche active (cmdk fait défiler l'option d'AVANT la recherche).
 */
const CommandList = React.forwardRef<
  React.ElementRef<typeof CommandPrimitive.List>,
  React.ComponentPropsWithoutRef<typeof CommandPrimitive.List>
>(({ className, ...props }, ref) => {
  const designeLOptionActive = useDesigneLOptionActive(ref, laListe, { defiler: true })
  return (
    <CommandPrimitive.List
      ref={designeLOptionActive}
      className={cn("max-h-[300px] overflow-y-auto overflow-x-hidden", className)}
      {...props}
    />
  )
})
CommandList.displayName = CommandPrimitive.List.displayName

/**
 * Le message de liste vide — et **la prop `when`, qui n'est pas un confort.**
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * 🔴 CE QUE `when` RÉPARE : un message qui ne s'affiche JAMAIS
 * ═══════════════════════════════════════════════════════════════════════════
 * `<Command.Empty>` de cmdk rend si et seulement si `filtered.count === 0`.
 * Or, cmdk 1.1.1 calcule ce compte ainsi (`dist/index.mjs`, verbatim) :
 *
 *     if (shouldFilter === false) { filtered.count = mountedItems.size; return }
 *     // sinon : count = nombre d'items dont le score de recherche est > 0
 *
 * Donc **avec `shouldFilter={false}` — le réglage de tout sélecteur qui cherche
 * côté SERVEUR — `filtered.count` n'est plus un compte de résultats : c'est un
 * compte d'items MONTÉS.** Le moindre `<CommandItem>` permanent (une entrée
 * « Aucun », « Retirer », « Ne pas rattacher », « Créer… ») le maintient à 1, et
 * `<CommandEmpty>` devient du **code mort** : la phrase qu'il contient n'atteint
 * jamais le DOM, et l'écran reste MUET sur une recherche infructueuse.
 *
 * ⚠️ Rien ne rougit. Le JSX est là, la clé i18n existe, la parité FR/EN est
 * verte, et le composant se monte sans erreur. Le défaut ne se voit qu'en
 * cherchant quelque chose qui n'existe pas — c'est-à-dire au seul moment où le
 * message servait.
 *
 * Mesuré le 2026-09-15 sur les 14 sélecteurs du dépôt qui combinent
 * `shouldFilter={false}` et `<CommandEmpty>` : **6 étaient touchés**, dont deux
 * dont le message n'avait jamais pu s'afficher une seule fois depuis leur
 * livraison (`LocationPicker`, `NonConformityPicker`). Les huit autres sont
 * sains — aucun item permanent.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * COMMENT S'EN SERVIR
 * ═══════════════════════════════════════════════════════════════════════════
 * Passer `when` avec la condition que le composant CONNAÎT déjà — presque
 * toujours « ma liste de résultats est vide » :
 *
 *     <CommandEmpty when={candidats.length === 0}>…</CommandEmpty>
 *
 * Le rendu cesse alors de dépendre d'un compte tenu par la librairie, et se
 * fonde sur la donnée. Sans `when`, le comportement historique est conservé à
 * l'identique — ce qui est juste tant qu'aucun item n'est monté en permanence.
 *
 * ⚠️ `when` est OBLIGATOIRE dès qu'un `<CommandItem>` est monté hors d'un
 * `.map()` sur les résultats, même sous condition : une sentinelle gardée par
 * `{value && …}` tue le message exactement quand on remplace un choix
 * existant. Le gardien
 * `components/ui/__tests__/command-empty-injoignable.test.tsx` porte la preuve
 * du mécanisme, dans les deux sens.
 */
const CommandEmpty = React.forwardRef<
  React.ElementRef<typeof CommandPrimitive.Empty>,
  React.ComponentPropsWithoutRef<typeof CommandPrimitive.Empty> & {
    /**
     * Rendre le message sur CETTE condition, au lieu de s'en remettre au compte
     * de cmdk. Indispensable sous `shouldFilter={false}` dès qu'un item est
     * monté en permanence — voir la docstring ci-dessus.
     */
    when?: boolean
  }
>(({ when, ...props }, ref) => {
  // ⚠️ `undefined` et `false` ne veulent PAS dire la même chose : `undefined`
  // signifie « je n'ai pas de condition, décide comme avant », `false`
  // signifie « il y a des résultats, ne rends rien ». Les confondre ferait
  // taire tous les appelants historiques.
  if (when === undefined) {
    return (
      <CommandPrimitive.Empty
        ref={ref}
        className="py-6 text-center text-sm"
        {...props}
      />
    )
  }
  if (!when) return null
  // Un `div` nu, et pas `Command.Empty` : le primitif REDEMANDERAIT le compte
  // à cmdk, ce qui annulerait exactement ce qu'on vient de contourner.
  return <div ref={ref} className="py-6 text-center text-sm" {...props} />
})
CommandEmpty.displayName = CommandPrimitive.Empty.displayName

const CommandGroup = React.forwardRef<
  React.ElementRef<typeof CommandPrimitive.Group>,
  React.ComponentPropsWithoutRef<typeof CommandPrimitive.Group>
>(({ className, ...props }, ref) => (
  <CommandPrimitive.Group
    ref={ref}
    className={cn(
      "overflow-hidden p-1 text-foreground [&_[cmdk-group-heading]]:px-2 [&_[cmdk-group-heading]]:py-1.5 [&_[cmdk-group-heading]]:text-xs [&_[cmdk-group-heading]]:font-medium [&_[cmdk-group-heading]]:text-muted-foreground",
      className
    )}
    {...props}
  />
))
CommandGroup.displayName = CommandPrimitive.Group.displayName

const CommandSeparator = React.forwardRef<
  React.ElementRef<typeof CommandPrimitive.Separator>,
  React.ComponentPropsWithoutRef<typeof CommandPrimitive.Separator>
>(({ className, ...props }, ref) => (
  <CommandPrimitive.Separator
    ref={ref}
    className={cn("-mx-1 h-px bg-border", className)}
    {...props}
  />
))
CommandSeparator.displayName = CommandPrimitive.Separator.displayName

const CommandItem = React.forwardRef<
  React.ElementRef<typeof CommandPrimitive.Item>,
  React.ComponentPropsWithoutRef<typeof CommandPrimitive.Item>
>(({ className, ...props }, ref) => (
  <CommandPrimitive.Item
    ref={ref}
    className={cn(
      "relative flex cursor-default gap-2 select-none items-center rounded-sm px-2 py-1.5 text-sm outline-none data-[disabled=true]:pointer-events-none data-[selected='true']:bg-accent data-[selected='true']:text-accent-foreground data-[disabled=true]:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0",
      className
    )}
    {...props}
  />
))
CommandItem.displayName = CommandPrimitive.Item.displayName

const CommandShortcut = ({
  className,
  ...props
}: React.HTMLAttributes<HTMLSpanElement>) => {
  return (
    <span
      className={cn(
        "ml-auto text-xs tracking-widest text-muted-foreground",
        className
      )}
      {...props}
    />
  )
}
CommandShortcut.displayName = "CommandShortcut"

export {
  Command,
  CommandDialog,
  CommandInput,
  CommandList,
  CommandEmpty,
  CommandGroup,
  CommandItem,
  CommandShortcut,
  CommandSeparator,
}
